import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: Record<string, any>;
  noIndex?: boolean;
}

export function stripHtmlTags(str?: string): string {
  if (!str) return '';
  return str
    // First decode HTML entities so escaped tags like &lt;p&gt; become <p>
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    // Second, strip all HTML tags (including <p>, </p>, <a>, etc.)
    .replace(/<[^>]*>/g, '')
    // Extra safety: remove any leftover literal p tag fragments
    .replace(/<\/?p[^>]*>/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const DEFAULT_DOMAIN = process.env.REACT_APP_PUBLIC_DOMAIN || 'https://www.4um.com';
const DEFAULT_TITLE = '4um - Connect People and Build Communities Online';
const DEFAULT_DESCRIPTION = 'Discover the only employee-owned user guided social media platform. Connect, chat, and study with a global community on 4um.';
const DEFAULT_OG_IMAGE = 'https://images.ctfassets.net/ewtdlsoyixc1/3K5S3OBYFYRDvOo4IrJYzf/37eb1ac3739ca2e6b80d94edb593be9d/4um_logo.png';

export const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  jsonLd,
  noIndex = false
}) => {
  const cleanTitleStr = stripHtmlTags(title);
  const cleanDescStr = stripHtmlTags(description || DEFAULT_DESCRIPTION);

  const metaTitle = cleanTitleStr
    ? cleanTitleStr.toLowerCase().includes('4um')
      ? cleanTitleStr
      : `${cleanTitleStr} | 4um`
    : DEFAULT_TITLE;
  const metaDescription = cleanDescStr || DEFAULT_DESCRIPTION;
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href : DEFAULT_DOMAIN);

  const cleanOgTitle = stripHtmlTags(ogTitle) || metaTitle;
  const cleanOgDescription = stripHtmlTags(ogDescription) || metaDescription;

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Robots indexing tag */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph Tags */}
      <meta property="og:site_name" content="4um" />
      <meta property="og:title" content={cleanOgTitle} />
      <meta property="og:description" content={cleanOgDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={cleanOgTitle} />
      <meta name="twitter:description" content={cleanOgDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Structured Data (JSON-LD) */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
